import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge } from 'antd';
import { useAuth } from '@shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { AppButton } from '@/components/atoms';
import { TENANT_ROUTES } from '@/constants/routes';
import { NOTIFICATION_CENTER_TITLE } from '@/types/admin-notification-center';
import { useClinicNotificationUnreadCounts } from '@/hooks/main/useClinicNotificationCenter';
import { convGtJDate } from '@/utils/dateConverter';

/**
 * Tenant Header Props
 */
interface TenantHeaderProps {
  onMenuClick?: () => void;
}

/**
 * Tenant Header Component
 */
export const TenantHeader: React.FC<TenantHeaderProps> = ({ onMenuClick }) => {
  const { user, logout, clinic } = useAuth();
  const { tenant } = useParams<{ tenant: string }>();
  const navigate = useNavigate();
  const { theme, colors } = useTheme();
  const siteType = clinic?.site_type || '';
  const isStaging = import.meta.env.VITE_APP_ENV === 'staging';
  /**
   * THE BELL COUNTS THE NEW 通知センター, not the legacy レセプトのお知らせ mailbox.
   *
   * Two tabs, one number: レセプト同期 + 審査システムからの通知. The old mailbox is still in the menu and still
   * fills up; it simply no longer owns the bell, because the bell now opens the screen these counts belong to.
   */
  const { data: notificationUnread } = useClinicNotificationUnreadCounts();
  const unreadCount = (notificationUnread?.rezept ?? 0) + (notificationUnread?.inbound ?? 0);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header
      className="text-white shadow sticky top-0 z-[1100]"
      style={{ backgroundColor: isStaging ? '#000000' : colors.primary }}
    >
      <div className="px-4">
        <div className="relative flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            {/* Menu Toggle Button */}
            <button
              onClick={onMenuClick}
              className="p-2 transition-colors rounded-md hover:bg-white/20"
              data-testid="menu-toggle"
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <Link to={`/${tenant}`} className="text-xl font-bold text-white">
              {theme.displayName}
            </Link>

            {/* Clinic Info - Date, Clinic ID, and Deadline */}
            {clinic && (
              <div className="flex items-center gap-3 px-3 py-1 ml-4 rounded">
                <div className="text-xs leading-relaxed text-white">
                  <div>{clinic.clinic_number}</div>
                  {siteType === 'medix' && clinic.limit_date && (
                    <div>
                      <span>提出期限:</span>
                      <span className="ml-1">{convGtJDate(clinic.limit_date)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {isStaging && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded bg-white px-4 py-1 text-lg font-bold text-black shadow-sm">
              ステージング環境
            </div>
          )}

          {/* User Menu */}
          {user && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-white">{user.name}</span>
              {/* 通知センター is three clicks deep in 月次業務; without this a failed 確定 sits unread until
                  someone happens to go looking. Same bell as the admin portal, pointed at the clinic's own
                  copy of the same screen. */}
              <button
                onClick={() => navigate(TENANT_ROUTES.NOTIFICATION_CENTER(tenant ?? ''))}
                className="p-2 transition-colors rounded-md hover:bg-white/20"
                aria-label={NOTIFICATION_CENTER_TITLE}
                data-testid="tenant-notification-center-bell"
              >
                {/* 999, the cap the tab badges use. Two caps on one screen would read as two counts. */}
                <Badge count={unreadCount} overflowCount={999} size="small">
                  <i className="fa fa-bell text-lg text-white" aria-hidden="true" />
                </Badge>
              </button>
              <AppButton
                type="default"
                onClick={handleLogout}
                style={{ backgroundColor: colors.secondary }}
                className="text-sm !text-white px-4 py-1.5 h-auto border border-white/30 hover:!text-white hover:opacity-90"
                data-testid="logout-button"
              >
                ログアウト
              </AppButton>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
